import { BreakpointObserver } from '@angular/cdk/layout';
import {
  CdkVirtualScrollViewport,
  ScrollingModule,
} from '@angular/cdk/scrolling';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  signal,
  untracked,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTreeModule } from '@angular/material/tree';
import {
  AiPreviewHandlerComponent,
  DocumentationOrigin,
  SiaAIChatService,
} from '@sia/ai';
import { SiaTextBoxComponent } from '@sia/inputs';
import {
  copyToClipboard,
  getDocumentIcon,
  notify,
  SiaLayoutService,
  SiaNewRouterService,
  SiaSizeDetectorService,
  SiaTooltipDirective,
  SiaTranslatePipe,
  SiaWindowSize,
} from '@sia/shared';
import { AiFileGridDetailsComponent } from '../../components/ai-file-grid-details/ai-file-grid-details.component';
import { AiGridFileComponent } from '../../components/ai-grid-file/ai-grid-file.component';
import { AiGridFilterComponent } from '../../components/ai-grid-filter/ai-grid-filter.component';
import { SiaGridFilterRequest } from '../../interfaces/ai-filters';
import { SiaFileDossier, SiaGridFile } from '../../interfaces/grid-file';
import { TruncateFilenamePipe } from '../../pipes/truncate-filename.pipe';
import { SiaAIFilesService } from '../../services/ai-files.service';
import { SiaRelatedFilesService } from '../../services/related-files-service';

@Component({
  selector: 'sia-ai-files-grid',
  imports: [
    FormsModule,
    MatTooltipModule,
    MatIcon,
    MatButtonModule,
    MatIconButton,
    MatMenuModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatTreeModule,
    MatDialogModule,
    SiaTranslatePipe,
    TruncateFilenamePipe,
    ScrollingModule,
    MatSelectModule,
    MatFormFieldModule,
    AiFileGridDetailsComponent,
    AiGridFilterComponent,
    AiGridFileComponent,
    SiaTextBoxComponent,
    AiPreviewHandlerComponent,
    SiaTooltipDirective,
  ],
  templateUrl: './ai-files-grid.component.html',
  styleUrls: ['./ai-files-grid.component.scss'],
})
export class SiaAIFilesGridComponent {
  #siaAIFilesService = inject(SiaAIFilesService);
  #destroyRef = inject(DestroyRef);
  #aiChatService = inject(SiaAIChatService);
  #breakpointObserver = inject(BreakpointObserver);
  #siaRelatedFilesService = inject(SiaRelatedFilesService);
  #siaLayoutService = inject(SiaLayoutService);
  #routerService = inject(SiaNewRouterService);
  #siaSizeDetectorService = inject(SiaSizeDetectorService);
  copyToClipboard = copyToClipboard;
  @ViewChild('aiFilesList')
  scrollContainer!: ElementRef<HTMLDivElement>;

  @ViewChild(CdkVirtualScrollViewport)
  viewport!: CdkVirtualScrollViewport;

  queryText = signal<string | undefined>(undefined);
  loadRelevantFiles = signal<boolean>(false);
  showMoreFilesVisible = signal<boolean>(false);

  viewMode = signal<'dossier' | 'standard'>('standard');
  dossier = signal<SiaFileDossier>({});
  updateNameDossier = signal<boolean>(false);

  // Pagination state
  private readonly ITEMS_PER_LOAD = 50;
  private _currentSkip = 0;
  private _totalCount = 0;
  private _hasMoreData = true;
  isSearching = signal<boolean>(false); // Full loading screen when searching
  isLoadingMore = signal<boolean>(false); // Simple spinner when loading more data

  gridData = signal<SiaGridFile[]>([]);
  relevantFiles = signal<string[]>([]);
  displayedFiles = signal<any[]>([]);

  // File che stanno entrando nella griglia (ritornano dal dossier)
  animatingInFiles = signal<Set<string>>(new Set());

  expandedItems = signal<Set<string>>(new Set());
  selectedItems = signal<SiaGridFile[]>([]);
  selectedItemsIds = computed(() => this.selectedItems().map((x) => x.sha256));

  sidebarFile = signal<SiaGridFile | undefined>(undefined);
  showPreview = signal<boolean>(false);

  // Fullscreen preview state
  fullscreenPreviewFile = signal<SiaGridFile | undefined>(undefined);
  fullscreenPreviewImages = signal<string[]>([]);
  isFullscreenPreview = signal<boolean>(false);

  // Mobile detection
  isMobile = signal<boolean>(false);

  // Computed signal to get selected files data - super optimized
  selectedFilesData = computed(() => {
    return this.selectedItems().map((x) => {
      let iconAndColor = getDocumentIcon(x.name, x.type);
      return {
        ...x,
        icon: iconAndColor.icon,
        color: iconAndColor.color,
        canPreview: iconAndColor.canPreview,
      };
    });
  });

  #requestFilters = signal<SiaGridFilterRequest[]>([]);
  #selectedCategoryIds = signal<string[]>([]);
  #filesToExclude = signal<string[]>([]);
  relevantFilesCount = signal<number>(0);
  aiGeneratedFilters = signal<{
    filters: SiaGridFilterRequest[];
    categories: string[];
  }>({
    filters: [],
    categories: [],
  });
  queryTextUsedForSearch = signal<string | undefined>(undefined);

  excludeDossier = effect(() => {
    const dossier = this.dossier();
    if (dossier && dossier.id) {
      const shaArray = dossier.files?.map((item) => item.sha256) ?? [];
      this.#filesToExclude.set(shaArray);
    } else {
      this.#filesToExclude.set([]);
    }
  });

  viewModeEffect = effect(() => {
    const dossier = this.viewMode();
    if (this.viewMode() === 'dossier') {
      this.#siaLayoutService.showAdditionalTitle(
        'DOSSIER',
        this.#routerService.getLatestRoutingData()?.guid || '',
      );
    } else {
      this.#siaLayoutService.showAdditionalTitle(
        '',
        this.#routerService.getLatestRoutingData()?.guid || '',
      );
    }
  });

  constructor() {
    // Observe screen size for mobile detection
    this.#siaSizeDetectorService.onResize$.subscribe(
      (size: SiaWindowSize | null) => {
        if (size) {
          this.isMobile.set(size.isMobile ?? false);
        }
      },
    );
  }

  ngOnInit() {
    this.resetAndLoadData();
  }

  async resetAndLoadData() {
    this._currentSkip = 0;
    this._totalCount = 0;
    this._hasMoreData = true;
    this.gridData.set([]);
    this.relevantFiles.set([]);
    this.displayedFiles.set([]);
    untracked(async () => {
      await this.loadMoreData();
    });
  }

  startToShowMoreFiles() {
    this.showMoreFilesVisible.set(false);
    this.loadRelevantFiles.set(false);
    this.loadMoreData(true);
  }

  loadLessRelatedFiles() {
    this.loadRelevantFiles.set(false);
    this.isSearching.set(true);
    this.loadMoreData(true);
  }
  setViewModeStandard() {
    this.viewMode.set('standard');
    this.dossier.set({});
    this.updateNameDossier.set(false);
  }
  async loadMoreData(forceLoadMore = false) {
    // Skip the loading check if we're forcing a load (e.g., from "Show more files" button)
    if (!forceLoadMore) {
      if (
        this.isSearching() ||
        this.isLoadingMore() ||
        (this.showMoreFilesVisible() && this._totalCount > 0)
      ) {
        return;
      }

      // Stop if all data has been loaded
      if (
        this._totalCount > 0 &&
        this.displayedFiles().length >= this._totalCount
      ) {
        return;
      }
    }

    // Determine if this is an initial search or loading more data
    const isInitialSearch = this._currentSkip === 0;

    // When forcing load more (from button), always use isLoadingMore
    if (forceLoadMore) {
      this.isLoadingMore.set(true);
    } else if (isInitialSearch) {
      this.isSearching.set(true);
      this.sidebarFile.set(undefined);
    } else {
      this.isLoadingMore.set(true);
    }

    const skip = this._currentSkip;
    const take = this.ITEMS_PER_LOAD;

    try {
      var res = await this.requestFiles(skip, take);

      let files = res.files ?? [];

      // Only set relevant files on the initial search, don't clear them when loading more
      if (this.loadRelevantFiles()) {
        this.relevantFiles.set(files.map((x) => x.sha256));
        this.relevantFilesCount.set(res.totalCount);
        this.aiGeneratedFilters.set({
          filters: res.smartFilters ?? [],
          categories: res.smartFiltersCategories ?? [],
        });
        if (res.totalCount > this.relevantFiles().length) {
          this.showMoreFilesVisible.set(true);
        }
      }
      this._totalCount = res.totalCount + this.relevantFiles().length;
      this._currentSkip += files.length - this.relevantFiles().length;

      // Update gridData with loaded files (without group headers)
      if (skip === 0) {
        this.gridData.set(files);
      } else {
        this.gridData.update((existing) => [...existing, ...files]);
      }
      this.displayedFiles.update((existing) => [...existing, ...files]);
    } catch (error) {
      console.error('Error loading files:', error);
    } finally {
      this.isSearching.set(false);
      this.isLoadingMore.set(false);
    }
  }

  private async requestFiles(skip: number, take: number) {
    let shaToExclude = [...this.relevantFiles(), ...this.#filesToExclude()];

    return await this.#siaAIFilesService.retriveFiles(
      this.queryText(),
      skip,
      skip + take,
      this.loadRelevantFiles(),
      shaToExclude.length > 0 ? shaToExclude : undefined,
      this.#selectedCategoryIds(),
      this.#requestFilters(),
    );
  }

  isRelevant(file: SiaGridFile): boolean {
    return this.relevantFiles().includes(file.sha256);
  }

  onScrollIndexChange(index: number) {
    const totalItems = this.displayedFiles().length;
    const threshold = Math.max(totalItems - 5, 0);
    if (index >= threshold) {
      this.loadMoreData();
    }
  }

  ngAfterViewInit() {
    this.viewport?.elementScrolled().subscribe(async () => {
      // Skip if already loading or no more data
      if (this.isSearching() || this.isLoadingMore() || !this._hasMoreData) {
        return;
      }
      const scrollOffset = this.viewport.measureScrollOffset('bottom');
      // Load more when within 300px of the bottom
      if (scrollOffset < 300) {
        const previousCount = this.displayedFiles().length;
        await this.loadMoreData();

        if (this.displayedFiles().length === previousCount) {
          this._hasMoreData = false;
        }
      }
    });
  }

  trackByIndex(index: number, item: any): any {
    return item.sha256 || index;
  }

  formatCategories(file: SiaGridFile): string {
    if (!file?.categories?.length) {
      return '—';
    }

    const descriptions = file.categories
      .map((category) => category?.description || category?.id || '')
      .map((value) => value?.trim())
      .filter((value) => !!value);

    return descriptions.length ? descriptions.join(', ') : '—';
  }

  closeFileDetails() {
    this.sidebarFile.set(undefined);
  }

  isGroupHeader(index: number, item: any): boolean {
    return item.isGroupHeader;
  }

  toggleSelection(item: SiaGridFile, checked: boolean) {
    let newSelected = this.selectedItems();

    if (checked) {
      newSelected.push(item);
    } else {
      newSelected = newSelected.filter((x) => x.sha256 !== item.sha256);
    }

    this.selectedItems.set([...newSelected]);
  }

  clearAllSelections() {
    this.selectedItems.set([]);
  }

  hasDetailsToShow(item: SiaGridFile): boolean {
    return true;
    // Check if preview panel is open (which means Size and Date columns are hidden and should be shown in details)
    // const hasHiddenColumns = this.fileForPreview() !== null;

    // // Check if item has extracted properties
    // const hasExtractedProperties =
    //   !!item.extractedProperties &&
    //   Object.keys(item.extractedProperties).length > 0;

    // // Show details button if either condition is true
    // return (
    //   hasHiddenColumns ||
    //   hasExtractedProperties ||
    //   item.path !== undefined ||
    //   item.size !== undefined
    // );
  }

  private formatPropertyValue(value: any): string {
    if (value === null || value === undefined) {
      return '—';
    }

    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }

    return String(value);
  }

  openChatWithSelectedDocuments() {
    this.#aiChatService.filesToChatWith.set(
      this.selectedFilesData().map(
        (f) =>
          ({
            fileSha256: f.sha256,
            fileName: f.name,
            icon: f.icon,
            color: f.color,
            canPreview: f.canPreview,
          }) as DocumentationOrigin,
      ),
    );
    this.selectedItems.set([]);
  }

  setSidebarFile(file: SiaGridFile) {
    this.sidebarFile.set(file);
    this.showPreview.set(false);
  }

  async setShowPreview(file: SiaGridFile) {
    if (this.viewMode() !== 'standard') {
      // In modalità dossier, apri la preview in fullscreen
      await this.openFullscreenPreview(file);
    } else {
      this.sidebarFile.set(file);
      this.showPreview.set(true);
    }
  }

  async openFullscreenPreview(file: SiaGridFile) {
    // Reset prima di aprire una nuova preview
    this.fullscreenPreviewImages.set([]);
    this.fullscreenPreviewFile.set(file);
    this.isFullscreenPreview.set(true);

    if (file.sha256 && file.name) {
      const fileInfo = await this.#aiChatService.retrieveOriginInformation(
        file.name.toLowerCase().split('.').pop() as string,
        file.sha256,
      );
      this.fullscreenPreviewImages.set(fileInfo?.images || []);
    }
  }

  closeFullscreenPreview() {
    this.isFullscreenPreview.set(false);
  }

  applyFilters(filters: {
    filters: SiaGridFilterRequest[];
    categories: string[];
  }) {
    this.#requestFilters.set([...filters.filters]);
    this.#selectedCategoryIds.set([...filters.categories]);
    this.loadRelevantFiles.set(!!this.queryText());
    this.queryTextUsedForSearch.set(this.queryText());
    this.resetAndLoadData();
  }

  addFileToDossier(file: SiaGridFile) {
    try {
      this.#siaRelatedFilesService.addFilesToDossier(this.dossier().id, [
        file.sha256,
      ]);
      const iconAndColor = getDocumentIcon(file.name, file.type);
      const fileWithIcon = {
        ...file,
        icon: iconAndColor.icon,
        color: iconAndColor.color,
        canPreview: iconAndColor.canPreview,
        _isAnimatingIn: true,
      };

      const currentDossier = this.dossier();
      const findFile = currentDossier.files?.find(
        (item) => item.sha256 === file.sha256,
      );
      if (findFile) return;

      // Aggiungi subito al dossier con animazione di entrata
      this.dossier.set({
        ...currentDossier,
        files: [...(currentDossier.files ?? []), fileWithIcon],
      });

      this.#filesToExclude.set([...this.#filesToExclude(), file.sha256]);

      // Rimuovi il file dalla griglia e il flag di animazione DOPO che l'animazione è completata
      setTimeout(() => {
        // Rimuovi dalla griglia principale
        this.displayedFiles.set(
          this.displayedFiles().filter((item) => item.sha256 !== file.sha256),
        );

        // Rimuovi il flag di animazione nel dossier
        const updatedDossier = this.dossier();
        this.dossier.set({
          ...updatedDossier,
          files: updatedDossier.files?.map((f) =>
            f.sha256 === file.sha256 ? { ...f, _isAnimatingIn: false } : f,
          ),
        });
      }, 400);

      notify(`File ${file.name} aggiunto con successo`, 2000, 'success');
    } catch (error) {
      notify(`Errore nell'aggiunta del file ${file.name} `, 2000, 'error');
    }
  }

  async saveDossierName() {
    try {
      await this.#siaRelatedFilesService.updateDossier(
        this.dossier().id,
        this.dossier().name,
      );

      this.updateNameDossier.set(false);
      notify(`Nome del dossier aggiornato con successo`, 2000, 'success');
    } catch (error) {
      notify(`Errore nell'aggiornamento del nome del dossier`, 2000, 'error');
    }
  }

  onFileRemovedFromDossier(file: SiaGridFile) {
    // Rimuovi il file dalla lista di esclusione
    this.#filesToExclude.set(
      this.#filesToExclude().filter((sha) => sha !== file.sha256),
    );

    // Marca il file per l'animazione di entrata
    this.animatingInFiles.update((set) => new Set(set).add(file.sha256));

    // Aggiungi il file alla griglia con il flag di animazione
    const fileWithAnimation = { ...file, _isAnimatingIn: true };
    this.displayedFiles.update((files) => [fileWithAnimation, ...files]);

    // Rimuovi il flag di animazione dopo che l'animazione è completata
    setTimeout(() => {
      this.animatingInFiles.update((set) => {
        const newSet = new Set(set);
        newSet.delete(file.sha256);
        return newSet;
      });
      this.displayedFiles.update((files) =>
        files.map((f) =>
          f.sha256 === file.sha256 ? { ...f, _isAnimatingIn: false } : f,
        ),
      );
    }, 400);
  }
}
